/**
 * Editor de foto antes de subir: drag para mover, slider de zoom, rotación 90°,
 * crop a aspect ratio cuadrado (como una lámina). Output: Blob JPEG listo para upload.
 */
import { useCallback, useState } from "react";
import Cropper, { type Area } from "react-easy-crop";
import { Icon } from "./Icon";

interface Props {
  imageSrc: string;        // dataURL o URL
  onCancel: () => void;
  onConfirm: (blob: Blob) => void | Promise<void>;
}

const ASPECT_SQUARE = 1;

export function PhotoEditor({ imageSrc, onCancel, onConfirm }: Props) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onCropComplete = useCallback((_area: Area, areaPixels: Area) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return;
    setSubmitting(true);
    try {
      const blob = await getCroppedBlob(imageSrc, croppedAreaPixels, rotation);
      await onConfirm(blob);
    } catch (e) {
      console.error("[PhotoEditor]", e);
      alert("Error procesando la imagen. Intenta de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 10000, display: "flex", flexDirection: "column" }}>
      <div style={{ padding: 14, color: "white", display: "flex", alignItems: "center", gap: 10 }}>
        <button
          className="icon-btn"
          onClick={onCancel}
          aria-label="Cancelar"
          style={{ background: "rgba(255,255,255,0.15)", color: "white" }}
          disabled={submitting}
        >
          <Icon name="x" size={18} />
        </button>
        <div style={{ flex: 1, fontWeight: 700, fontFamily: "var(--font-display)" }}>Ajustar foto</div>
        <div style={{ fontSize: 12, opacity: 0.7 }}>Arrastra · Pellizca para zoom</div>
      </div>

      <div style={{ flex: 1, position: "relative", background: "#000" }}>
        <Cropper
          image={imageSrc}
          crop={crop}
          zoom={zoom}
          rotation={rotation}
          aspect={ASPECT_SQUARE}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onRotationChange={setRotation}
          onCropComplete={onCropComplete}
          objectFit="contain"
          showGrid={true}
          minZoom={0.5}
          maxZoom={4}
          zoomSpeed={1}
          restrictPosition={false}
        />
      </div>

      <div style={{ padding: 14, background: "rgba(0,0,0,0.92)", color: "white" }}>
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, marginBottom: 4 }}>
            <span>🔍</span>
            <span>Zoom</span>
            <span style={{ flex: 1 }} />
            <span style={{ opacity: 0.7 }}>{zoom.toFixed(1)}×</span>
          </div>
          <input
            type="range"
            min={0.5}
            max={4}
            step={0.05}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            style={{ width: "100%" }}
            disabled={submitting}
          />
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 12, justifyContent: "space-between" }}>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => setRotation((r) => (r - 90 + 360) % 360)}
            style={{ background: "rgba(255,255,255,0.15)", color: "white", border: 0 }}
            disabled={submitting}
          >
            ↺ Rotar
          </button>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => setRotation((r) => (r + 90) % 360)}
            style={{ background: "rgba(255,255,255,0.15)", color: "white", border: 0 }}
            disabled={submitting}
          >
            ↻ Rotar
          </button>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => { setCrop({ x: 0, y: 0 }); setZoom(1); setRotation(0); }}
            style={{ background: "rgba(255,255,255,0.15)", color: "white", border: 0 }}
            disabled={submitting}
          >
            ⟲ Reset
          </button>
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <button
            className="btn btn-secondary"
            onClick={onCancel}
            style={{ flex: 1, justifyContent: "center", background: "rgba(255,255,255,0.15)", color: "white", border: 0 }}
            disabled={submitting}
          >
            Cancelar
          </button>
          <button
            className="btn btn-primary"
            onClick={handleConfirm}
            style={{ flex: 1, justifyContent: "center" }}
            disabled={submitting || !croppedAreaPixels}
          >
            {submitting ? "Subiendo…" : "✓ Usar esta foto"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Recorta + rota la imagen y devuelve un Blob JPEG. */
async function getCroppedBlob(
  src: string,
  pixelCrop: Area,
  rotation: number,
): Promise<Blob> {
  const image = await createImage(src);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("No 2d context");

  const radians = (rotation * Math.PI) / 180;
  const sin = Math.abs(Math.sin(radians));
  const cos = Math.abs(Math.cos(radians));
  const bWidth = image.width * cos + image.height * sin;
  const bHeight = image.width * sin + image.height * cos;

  canvas.width = bWidth;
  canvas.height = bHeight;
  ctx.translate(bWidth / 2, bHeight / 2);
  ctx.rotate(radians);
  ctx.drawImage(image, -image.width / 2, -image.height / 2);

  // Now extract the crop area
  const data = ctx.getImageData(pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height);
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  ctx.putImageData(data, 0, 0);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("toBlob failed"));
    }, "image/jpeg", 0.9);
  });
}

function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", (err) => reject(err));
    img.crossOrigin = "anonymous";
    img.src = url;
  });
}
